/** What the knowledge screens share (plan E07-T04): statuses, the embedder, a new collection, search. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useBff, useMe } from "../app/session";
import { useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";
import { useEndpoints } from "../agents/builder/models";

export type Collection = Schemas["CollectionOut"];
export type KnowledgeDocument = Schemas["DocumentOut"];
type Embedder = Schemas["EmbedderSpec"];
type Provider = Embedder["provider"];

export const field = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";
const PROVIDERS: Provider[] = ["openai", "openai-compatible", "endpoint", "fake"];
const MODEL_HINT: Record<Provider, string> = { "openai": "text-embedding-3-small", "openai-compatible": "BAAI/bge-m3", "endpoint": "", "fake": "" };

export function DocumentStatus({ status }: { status: KnowledgeDocument["status"] }) {
  const t = useT();
  const tone = status === "ready" ? "border-[var(--color-success)]" : status === "failed" ? "border-[var(--color-danger)]"
    : "border-[var(--color-warning)]";
  return <span className={`rounded-full border px-2 py-0.5 text-xs ${tone}`}>{t(`knowledge.status.${status}` as MessageId)}</span>;
}

export function embedderLabel(e: Embedder): string {
  if (e.provider === "endpoint") return `endpoint · ${e.endpoint ?? ""}`;
  return e.provider === "fake" ? "fake" : `${e.provider} · ${e.model ?? ""}`;
}

/** Name, description and embedder: the embedder is tried by the API before the collection exists. */
export function NewCollection({ onDone }: { onDone?: () => void }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const navigate = useNavigate();
  const id = useId();
  const [form, setForm] = useState({ name: "", description: "", provider: "openai" as Provider, model: "", credential: "", endpoint: "",
    askDimensions: false });
  const endpoints = useEndpoints("embedding");
  const own = form.provider === "endpoint"; // a tenant's model endpoint (E10)
  const hosted = form.provider !== "fake" && !own;
  const compatible = form.provider === "openai-compatible"; // OpenAI itself is always asked for 1024
  const endpoint = form.endpoint || endpoints[0]?.value || "";
  const create = useMutation({
    mutationFn: () => data(api.POST("/v1/knowledge/collections", {
      body: {
        name: form.name.trim(), description: form.description,
        embedder: own ? { provider: "endpoint", endpoint }
          : hosted ? { provider: form.provider, model: form.model.trim(), credential: form.credential.trim(),
              ...(compatible && form.askDimensions ? { ask_dimensions: true } : {}) }
          : { provider: "fake" },
      },
    })),
    onSuccess: async (made) => {
      await queries.invalidateQueries({ queryKey: ["knowledge", tenant] });
      onDone?.();
      await navigate({ to: "/knowledge/$collectionId", params: { collectionId: made.id } });
    },
  });
  const ready = form.name.trim() && (own ? endpoint : !hosted || (form.model.trim() && form.credential.trim()));
  return (
    <form aria-label={t("knowledge.new")} className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (ready) create.mutate();
      }}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor={`${id}-name`} className="block text-sm font-medium">{t("knowledge.name")}</label>
          <input id={`${id}-name`} className={`${field} w-full`} value={form.name} maxLength={100} required
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-description`} className="block text-sm font-medium">{t("knowledge.descriptionLabel")}</label>
          <input id={`${id}-description`} className={`${field} w-full`} value={form.description} maxLength={1000}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-provider`} className="block text-sm font-medium">{t("knowledge.embedder")}</label>
          <select id={`${id}-provider`} className={`${field} w-full`} value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value as Provider })}>
            {PROVIDERS.map((p) => <option key={p} value={p}>{t(`knowledge.provider.${p}` as MessageId)}</option>)}
          </select>
          <p className="text-xs text-[var(--color-muted)]">{t("knowledge.embedderHint")}</p>
        </div>
        {own && (
          <div className="space-y-1">
            <label htmlFor={`${id}-endpoint`} className="block text-sm font-medium">{t("knowledge.endpoint")}</label>
            <select id={`${id}-endpoint`} className={`${field} w-full`} value={endpoint} aria-describedby={`${id}-endpoint-hint`}
              onChange={(e) => setForm({ ...form, endpoint: e.target.value })}>
              {endpoints.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <p id={`${id}-endpoint-hint`} className="text-xs text-[var(--color-muted)]">
              {endpoints.length ? t("knowledge.endpointHint") : t("builder.noEndpoints", { kind: "embedding" })}
            </p>
          </div>
        )}
        {hosted && (
          <>
            <div className="space-y-1">
              <label htmlFor={`${id}-model`} className="block text-sm font-medium">{t("knowledge.model")}</label>
              <input id={`${id}-model`} className={`${field} w-full font-mono`} value={form.model} placeholder={MODEL_HINT[form.provider]}
                maxLength={200} required onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label htmlFor={`${id}-credential`} className="block text-sm font-medium">{t("knowledge.credential")}</label>
              <input id={`${id}-credential`} className={`${field} w-full font-mono`} value={form.credential} maxLength={100} required
                aria-describedby={`${id}-credential-hint`} onChange={(e) => setForm({ ...form, credential: e.target.value })} />
              <p id={`${id}-credential-hint`} className="text-xs text-[var(--color-muted)]">
                {t(form.provider === "openai" ? "knowledge.credentialHint" : "knowledge.credentialHintCompatible")}
              </p>
            </div>
            {compatible && (
              <label className="flex items-start gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={form.askDimensions} className="mt-0.5 size-4 accent-[var(--color-primary)]"
                  aria-describedby={`${id}-dimensions-hint`} onChange={(e) => setForm({ ...form, askDimensions: e.target.checked })} />
                <span>
                  {t("knowledge.askDimensions")}
                  <span id={`${id}-dimensions-hint`} className="block text-xs text-[var(--color-muted)]">{t("knowledge.askDimensionsHint")}</span>
                </span>
              </label>
            )}
          </>
        )}
      </div>
      {create.isError && <ErrorState error={create.error} />}
      <Button type="submit" disabled={!ready || create.isPending}>
        {create.isPending ? t("knowledge.checking") : t("knowledge.create")}
      </Button>
    </form>
  );
}

/** The search an agent's search_knowledge runs: scores, both rankings, and where each passage is from. */
export function SearchPlayground({ collectionId }: { collectionId: string }) {
  const t = useT();
  const { api } = useBff();
  const id = useId();
  const [query, setQuery] = useState("");
  const [topK, setTopK] = useState(5);
  const search = useMutation({
    mutationFn: () => data(api.POST("/v1/knowledge/search", { body: { query: query.trim(), collections: [collectionId], top_k: topK } })),
  });
  return (
    <section aria-labelledby={`${id}-title`} className="space-y-3">
      <h2 id={`${id}-title`} className="text-lg font-semibold">{t("knowledge.search")}</h2>
      <form role="search" aria-label={t("knowledge.search")} className="flex flex-wrap items-end gap-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (query.trim()) search.mutate();
        }}>
        <div className="min-w-64 flex-1 space-y-1">
          <label htmlFor={`${id}-query`} className="block text-sm font-medium">{t("knowledge.query")}</label>
          <input id={`${id}-query`} className={`${field} w-full`} value={query} maxLength={1000}
            placeholder={t("knowledge.queryPlaceholder")} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-k`} className="block text-sm font-medium">{t("knowledge.topK")}</label>
          <input id={`${id}-k`} type="number" min={1} max={20} className={`${field} w-20`} value={topK}
            onChange={(e) => setTopK(Math.min(20, Math.max(1, Number(e.target.value) || 1)))} />
        </div>
        <Button type="submit" disabled={!query.trim() || search.isPending}>{t("knowledge.searchButton")}</Button>
      </form>
      {search.isError && <ErrorState error={search.error} />}
      {search.data && (
        search.data.results.length === 0 ? (
          <p className="text-sm text-[var(--color-muted)]">{t("knowledge.nothingFound")}</p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-[var(--color-muted)]">{t("knowledge.took", { ms: Math.round(search.data.took_ms) })}</p>
            <ol aria-label={t("knowledge.results")} className="space-y-2">
              {search.data.results.map((hit, i) => (
                <li key={hit.chunk_id} className="space-y-1 rounded-lg border border-[var(--color-border)] p-3 text-sm">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium">{i + 1}. {hit.title}</span>
                    <span className="text-xs text-[var(--color-muted)]">
                      {t("knowledge.score", {
                        score: hit.score.toFixed(4), vector: hit.ranks.vector ?? "—", text: hit.ranks.text ?? "—",
                      })}
                    </span>
                  </div>
                  <p className="whitespace-pre-line">{hit.text}</p>
                  <Where metadata={hit.metadata} />
                </li>
              ))}
            </ol>
          </div>
        )
      )}
    </section>
  );
}

/** Where in its document a passage is: a page, a heading, a row, a page of a site. */
export function Where({ metadata }: { metadata: Record<string, unknown> }) {
  const t = useT();
  const parts = [
    metadata.page !== undefined ? t("knowledge.where.page", { page: String(metadata.page) }) : "",
    typeof metadata.heading === "string" ? t("knowledge.where.heading", { heading: metadata.heading }) : "",
    metadata.row !== undefined ? t("knowledge.where.row", { row: String(metadata.row) }) : "",
    typeof metadata.url === "string" ? metadata.url : "",
  ].filter(Boolean);
  return parts.length ? <p className="break-all text-xs text-[var(--color-muted)]">{parts.join(" · ")}</p> : null;
}
